/// <amd-module name="typeahead/client/ClientHandlerRegistry" />
/// <amd-dependency path="typeahead/core/context" name="coreContext"/>
import {GeolocationDataProvider} from "typeahead/core/geolocation/GeolocationDataProvider";
import {GeolocationViewController} from "typeahead/core/geolocation/GeolocationViewController";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {FormSubmittedMessage} from "typeahead/core/message/FormSubmittedMessage";
import {SubmitClickedMessage} from "typeahead/core/message/SubmitClickedMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {UserExitedMessage} from "typeahead/core/message/UserExitedMessage";
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";
import {Optional} from "typeahead/core/util/Optional";

export interface IClientHandler {
    isApplicable(clientId: string): boolean;
    subscribeFormLoadedEvent(): void;
    subscribeFormSubmitEvent(): void;
    getTab(): string;
    getGeolocationViewController(geolocationDataProvider: GeolocationDataProvider): GeolocationViewController;
}

export interface IHsrpSubmitData {
    origin: string;
    query: string;
    isValid: boolean;
}

/* ILobData is the type of data that is published by Storefront along with the message 'sfSelectedLobs.updated'
    when lob changes */
export interface ILobData {
    page: string;
    searchType: string;
    searchSubType: string;
}

export class ClientHandlerRegistry {

    /**
     * HSRP publishes certain messages in an order during submit. "hsr.submit.showErrors" is published after client
     * validation. The subscriber of this message can read the isValid property from the 'submitObject' which is also
     * part of the message and perform certain operations based on the value of this flag.
     */
    public static readonly HSR_SUBMIT_SHOW_ERRORS_MESSAGE = "hsr.submit.showErrors";
    /**
     * "hsr.submit.getQuery" is published during submit by HSRP after "hsr.submit.showErrors" if isValid flag in
     * 'submitObject' is true. The subscriber of this message in HSRP reads all the form elements and creates the
     * request query. This query is set into the 'query' property in the submitObject.
     */
    public static readonly HSR_SUBMIT_GET_QUERY_MESSAGE = "hsr.submit.getQuery";

    /**
     * "hsr.submit.beforeUpdateHash" is published after the "hsr.submit.getQuery" event
     * to add anything that isn't already there for the hash update in the query
     */
    public static readonly HSR_SUBMIT_BEFORE_UPDATE_HASH_MESSAGE = "hsr.submit.beforeUpdateHash";

    public static handlePageLoad() {
        ClientHandlerRegistry.client = ClientHandlerRegistry.getClientHandler(TypeaheadUtils.getClientId());
        if (ClientHandlerRegistry.client.isPresent()) {
            ClientHandlerRegistry.client.get().subscribeFormLoadedEvent();
            ClientHandlerRegistry.client.get().subscribeFormSubmitEvent();
        } else {
            TypeaheadErrorLogger.log(
                new Error("No client handler for this page. Omniture logs may not work."),
                Severity.Excusable, ProblemOrigin.UniversalTypeahead);
        }
    }

    public static getTab(): string {
        return ClientHandlerRegistry.client.isPresent() ? ClientHandlerRegistry.client.get().getTab() : undefined;
    }

    public static registerClient(client: IClientHandler) {
        if (ClientHandlerRegistry.allClients.indexOf(client) < 0) {
            ClientHandlerRegistry.allClients.push(client);
        }
    }
    public static publishSubmitClickedMessage(rfrrIdPrefix: string, event?: JQueryEventObject): void {
        TypeaheadMessage.publish(new SubmitClickedMessage(rfrrIdPrefix, coreContext, event));
        // Sending a undefined inputId to indicate no typeahead interaction
        TypeaheadMessage.publish(new UserExitedMessage());
    }

    public static publishFormSubmittedMessage(isFormValid: boolean, event?: JQueryEventObject): void {
        TypeaheadMessage.publish(new FormSubmittedMessage(isFormValid, coreContext, event));
    }

    public static getGeolocationViewController(geolocationDataProvider: GeolocationDataProvider):
                                                                                        GeolocationViewController {
        return ClientHandlerRegistry.client.isPresent() ?
            ClientHandlerRegistry.client.get().getGeolocationViewController(geolocationDataProvider) : undefined;
    }

    private static allClients: IClientHandler[] = [];
    private static client: Optional<IClientHandler> = Optional.ofNullable(undefined);

    private static getClientHandler(clientId: string): Optional<IClientHandler> {
        let clientHandler: IClientHandler;
        if (clientId) {
            for (let i: number = 0; i < ClientHandlerRegistry.allClients.length; i++) {
                if (ClientHandlerRegistry.allClients[i].isApplicable(clientId)) {
                    clientHandler = ClientHandlerRegistry.allClients[i];
                    break;
                }
            }
        }
        return Optional.ofNullable(clientHandler);
    }
}
